import React, { useEffect, useMemo, useState } from "react";
import { useCreateDeviceMutation } from "../../devices/apiSlice";
import {
  useCreateAlertProfileMutation,
  useGetAlertProfilesQuery,
  type AlertLevel,
} from "../../alert-profiles/apiSlice";
import { MonitoringDevice } from "../../../types/domain";
import { RealWorldCoordinate } from "../types";
import { isMovementType } from "../../../utils/movementTypes";
import {
  DEFAULT_VIBRATION_PROFILE_DESCRIPTION,
  sortVibrationProfiles,
} from "../../../utils/vibrationProfiles";

interface DevicePlacementModalProps {
  show: boolean;
  projectId: number;
  /** Real-world coordinate derived from the clicked layout position. */
  coordinate: RealWorldCoordinate | null;
  onClose: () => void;
  onCreated: () => void;
}

const TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "ground_movement", label: "Ground Movement" },
  { value: "building_movement", label: "Building Movement" },
  { value: "utility_movement", label: "Utility Movement" },
  { value: "ground_water", label: "Ground Water" },
  { value: "tilting", label: "Tilting" },
  { value: "vibration", label: "Vibration" },
  { value: "crack", label: "Crack Gauge" },
];

// Returns the label + unit for pre-existing value (null = no field shown)
function preExistingLabel(deviceType: string): string | null {
  switch (deviceType) {
    case "ground_settlement":
    case "building_settlement":
    case "utility_settlement":
      return "Pre-existing value (mm)";
    case "crack":
      return "Pre-existing value X (mm)";
    case "ground_water":
      return "Pre-existing value (m)";
    case "tilting":
      return "Pre-existing value (1:N — enter N)";
    case "vibration":
      return null;
    case "movement":
    case "ground_movement":
    case "building_movement":
    case "utility_movement":
      return "Pre-existing settlement (mm)";
    default:
      return "Pre-existing value";
  }
}

const DevicePlacementModal: React.FC<DevicePlacementModalProps> = ({
  show,
  projectId,
  coordinate,
  onClose,
  onCreated,
}) => {
  const [createDevice, { isLoading }] = useCreateDeviceMutation();
  const [createAlertProfile] = useCreateAlertProfileMutation();

  const { data: profiles } = useGetAlertProfilesQuery(projectId, {
    skip: !projectId,
  });
  const [deviceName, setDeviceName] = useState("");
  const [deviceType, setDeviceType] = useState("ground_movement");
  const [easting, setEasting] = useState("");
  const [northing, setNorthing] = useState("");
  const [installDate, setInstallDate] = useState("");
  // Pre-existing value (X for crack); default 0
  const [preExisting, setPreExisting] = useState("0");
  // Pre-existing value Y (crack only); default 0
  const [preExisting2, setPreExisting2] = useState("0");
  // Pre-existing easting change (movement only); default 0
  const [preExisting3, setPreExisting3] = useState("0");
  // Ground water alert levels
  const [gwLowest, setGwLowest] = useState("0");
  const [gwAboveLowest, setGwAboveLowest] = useState("0");
  const [gwBelowHighest, setGwBelowHighest] = useState("0");
  const [gwHighest, setGwHighest] = useState("0");
  // Movement configuration (movement type only)
  const [movementMode, setMovementMode] = useState<"1" | "2" | "3">("3");
  // Tilting configuration
  const [tiltEntryMode, setTiltEntryMode] = useState<
    "slope_ratio" | "movement"
  >("slope_ratio");
  const [tiltHeight, setTiltHeight] = useState("");
  const [tiltPreMovement, setTiltPreMovement] = useState("0");
  const [vibrationProfileId, setVibrationProfileId] = useState("");

  const [error, setError] = useState<string | null>(null);
  const vibrationProfiles = useMemo(
    () => sortVibrationProfiles(profiles),
    [profiles],
  );

  useEffect(() => {
    if (show) {
      setDeviceName("");
      setDeviceType("ground_movement");
      setInstallDate("");
      setPreExisting("0");
      setPreExisting2("0");
      setPreExisting3("0");
      setGwLowest("0");
      setGwAboveLowest("0.5");
      setGwBelowHighest("0.5");
      setGwHighest("0");
      setMovementMode("1");
      setTiltEntryMode("slope_ratio");
      setTiltHeight("");
      setTiltPreMovement("0");
      setVibrationProfileId("");
      setError(null);
    }
  }, [show]);

  useEffect(() => {
    if (deviceType !== "vibration") return;
    if (vibrationProfiles.length === 0) return;
    if (
      vibrationProfileId &&
      vibrationProfiles.some(
        (profile) => String(profile.id) === vibrationProfileId,
      )
    ) {
      return;
    }

    const defaultProfile =
      vibrationProfiles.find(
        (profile) =>
          profile.description === DEFAULT_VIBRATION_PROFILE_DESCRIPTION,
      ) ?? vibrationProfiles[0];
    setVibrationProfileId(String(defaultProfile.id));
  }, [deviceType, vibrationProfiles, vibrationProfileId]);

  useEffect(() => {
    if (coordinate) {
      setEasting(String(Math.round(coordinate.easting)));
      setNorthing(String(Math.round(coordinate.northing)));
    }
  }, [coordinate]);

  const canSave =
    deviceName.trim() !== "" &&
    easting !== "" &&
    northing !== "" &&
    (deviceType !== "tilting" ||
      tiltEntryMode !== "movement" ||
      tiltHeight !== "") &&
    (deviceType !== "vibration" || vibrationProfileId !== "");

  const handleSave = async () => {
    if (!canSave) return;
    setError(null);

    const base: Partial<MonitoringDevice> = {
      device_name: deviceName.trim(),
      device_type: deviceType,
      real_easting: Number(easting),
      real_northing: Number(northing),
      monitoring_frequency: "daily",
      installation_date: installDate || undefined,
      project_id: projectId,
    };

    // Attach pre-existing values
    if (deviceType === "tilting") {
      if (tiltEntryMode === "movement") {
        (base as any).tilt_entry_mode = "movement";
        (base as any).tilt_height =
          tiltHeight !== "" ? Number(tiltHeight) : null;
        (base as any).tilt_pre_existing_movement = Number(tiltPreMovement) || 0;
        // Pre-existing tilt is captured as a movement; keep the N-based field 0.
        (base as any).pre_existing_value = 0;
      } else {
        (base as any).tilt_entry_mode = "slope_ratio";
        (base as any).pre_existing_value = Number(preExisting);
      }
    } else if (deviceType === "crack") {
      (base as any).pre_existing_value = Number(preExisting);
      (base as any).pre_existing_value2 = Number(preExisting2);
    } else if (isMovementType(deviceType)) {
      (base as any).pre_existing_value = Number(preExisting) / 1000;
      (base as any).pre_existing_value2 = Number(preExisting2);
      (base as any).pre_existing_value3 = Number(preExisting3);
      (base as any).movement_mode = Number(movementMode);
      // Auto-link movement defaults by both axis and movement checkpoint scope
      // (ground/building/utility), then fall back to legacy generic defaults.
      const movementScope =
        deviceType === "ground_movement"
          ? "ground"
          : deviceType === "building_movement"
            ? "building"
            : deviceType === "utility_movement"
              ? "utility"
              : null;
      const findDefault = (
        type: "movement_settlement" | "movement_lateral",
      ) => {
        const list = (profiles ?? []).filter((p) => p.profile_type === type);
        if (list.length === 0) return undefined;

        if (movementScope) {
          const scopeKey = `${movementScope} movement`;
          const scoped = list.find((p) =>
            p.description.toLowerCase().includes(scopeKey),
          );
          if (scoped) return scoped;
        }

        return (
          list.find((p) => p.description.toLowerCase().startsWith("default")) ??
          list[0]
        );
      };
      const settlementDefault = findDefault("movement_settlement");
      const lateralDefault = findDefault("movement_lateral");

      // Keep settlement profile populated even for lateral-only mode so
      // movement triggers can be generated consistently in summary output.
      (base as any).alert_profile_id = settlementDefault?.id ?? null;
      (base as any).movement_lateral_alert_profile_id = ["2", "3"].includes(
        movementMode,
      )
        ? (lateralDefault?.id ?? null)
        : null;
    } else if (deviceType !== "vibration") {
      const rawValue = Number(preExisting);
      (base as any).pre_existing_value = [
        "ground_settlement",
        "building_settlement",
        "utility_settlement",
      ].includes(deviceType)
        ? rawValue / 1000
        : rawValue;
    }

    try {
      if (deviceType === "ground_water") {
        const lowest = Number(gwLowest);
        const aboveLowest = Number(gwAboveLowest);
        const belowHighest = Number(gwBelowHighest);
        const highest = Number(gwHighest);

        if (
          !Number.isFinite(lowest) ||
          !Number.isFinite(aboveLowest) ||
          !Number.isFinite(belowHighest) ||
          !Number.isFinite(highest)
        ) {
          setError("Ground water alert levels must be valid numbers.");
          return;
        }

        if (highest <= lowest) {
          setError(
            "Ground water validation failed: Highest must be greater than Lowest.",
          );
          return;
        }

        if (highest - belowHighest <= lowest + aboveLowest) {
          setError(
            "Ground water validation failed: (Highest - Level below highest) must be greater than (Lowest + Level above lowest).",
          );
          return;
        }

        const gwLevels: AlertLevel[] = [
          {
            order: 1,
            name: "Lowest",
            value: lowest,
            entry_mode: "value",
          },
          {
            order: 2,
            name: "Level above the lowest",
            value: aboveLowest,
            entry_mode: "value",
          },
          {
            order: 3,
            name: "Level below the highest",
            value: belowHighest,
            entry_mode: "value",
          },
          {
            order: 4,
            name: "Highest",
            value: highest,
            entry_mode: "value",
          },
        ];
        const profile = await createAlertProfile({
          project_id: projectId,
          description: `GW: ${deviceName.trim()}`,
          profile_type: "ground_water",
          alert_level_json: gwLevels,
        }).unwrap();
        await createDevice({ ...base, alert_profile_id: profile.id }).unwrap();
      } else if (isMovementType(deviceType)) {
        // base already carries the settlement + lateral profiles.
        await createDevice({ ...base }).unwrap();
      } else if (deviceType === "vibration") {
        const profile = vibrationProfiles.find(
          (p) => String(p.id) === vibrationProfileId,
        );
        if (!profile) {
          setError("Please select a vibration action profile.");
          return;
        }
        await createDevice({ ...base, alert_profile_id: profile.id }).unwrap();
      } else {
        // Find a matching action profile: prefer one starting with "Default", fall back to any same type
        const matching = (profiles ?? []).filter(
          (p) => p.profile_type === deviceType,
        );
        if (matching.length === 0) {
          setError(
            `No action profile found for this checkpoint type. Please create one in Action Profiles first.`,
          );
          return;
        }
        const chosen =
          matching.find((p) =>
            p.description.toLowerCase().startsWith("default"),
          ) ?? matching[0];
        await createDevice({ ...base, alert_profile_id: chosen.id }).unwrap();
      }
      onCreated();
      onClose();
    } catch {
      setError("Failed to create checkpoint.");
    }
  };

  const preLabel = preExistingLabel(deviceType);

  return (
    <div className={`modal-backdrop ${show ? "show" : ""}`}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-head">
          <h3>Place New Checkpoint</h3>
          <button type="button" className="btn btn-sm" onClick={onClose}>
            <i className="bi bi-x-lg" />
          </button>
        </div>
        <div className="modal-body">
          {error && (
            <div className="badge alarm" style={{ marginBottom: 12 }}>
              {error}
            </div>
          )}
          <div className="grid" style={{ gap: 12 }}>
            <div className="field">
              <label>Checkpoint name</label>
              <input
                className="input"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="e.g. GS-01"
              />
            </div>
            <div className="field">
              <label>Checkpoint type</label>
              <select
                className="select"
                value={deviceType}
                onChange={(e) => {
                  setDeviceType(e.target.value);
                  setPreExisting("0");
                  setPreExisting2("0");
                  setPreExisting3("0");
                  setVibrationProfileId("");
                }}
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid cols-2" style={{ gap: 12 }}>
              <div className="field">
                <label>Northing (m)</label>
                <input
                  className="input"
                  type="number"
                  value={northing}
                  onChange={(e) => setNorthing(e.target.value)}
                />
              </div>
              <div className="field">
                <label>Easting (m)</label>
                <input
                  className="input"
                  type="number"
                  value={easting}
                  onChange={(e) => setEasting(e.target.value)}
                />
              </div>
            </div>
            <div className="field">
              <label>Installation date (optional)</label>
              <input
                className="input"
                type="date"
                value={installDate}
                onChange={(e) => setInstallDate(e.target.value)}
              />
            </div>

            {deviceType === "vibration" && (
              <div className="field">
                <label>Action profile</label>
                <select
                  className="select"
                  value={vibrationProfileId}
                  onChange={(e) => setVibrationProfileId(e.target.value)}
                >
                  {vibrationProfiles.length === 0 ? (
                    <option value="">No vibration profiles available</option>
                  ) : (
                    vibrationProfiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.description}
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}

            {/* Tilting entry mode (create-only, immutable after) */}
            {deviceType === "tilting" && (
              <div className="field">
                <label>Entry mode</label>
                <select
                  className="select"
                  value={tiltEntryMode}
                  onChange={(e) =>
                    setTiltEntryMode(
                      e.target.value as "slope_ratio" | "movement",
                    )
                  }
                >
                  <option value="slope_ratio">tiltmeter mode</option>
                  <option value="movement">Plumb-wire Mode</option>
                </select>
                <span
                  style={{
                    fontSize: "0.78em",
                    color: "var(--text-muted, #888)",
                    marginTop: 3,
                    display: "block",
                  }}
                >
                  Cannot be changed after the checkpoint is created.
                </span>
              </div>
            )}

            {/* Tilting movement-mode fields: constant height + pre-existing movement */}
            {deviceType === "tilting" && tiltEntryMode === "movement" && (
              <div className="grid cols-2" style={{ gap: 12 }}>
                <div className="field">
                  <label>Height (m)</label>
                  <input
                    className="input"
                    type="number"
                    step="any"
                    value={tiltHeight}
                    onChange={(e) => setTiltHeight(e.target.value)}
                    placeholder="e.g. 3"
                  />
                </div>
                <div className="field">
                  <label>Pre-existing movement (mm)</label>
                  <input
                    className="input"
                    type="number"
                    step="any"
                    value={tiltPreMovement}
                    onChange={(e) => setTiltPreMovement(e.target.value)}
                  />
                  {Number(tiltHeight) > 0 && Number(tiltPreMovement) !== 0 && (
                    <small style={{ color: "var(--text-muted, #888)" }}>
                      Slope ratio 1 :{" "}
                      {Math.round(
                        (Number(tiltHeight) * 1000) /
                          Math.abs(Number(tiltPreMovement)),
                      )}
                    </small>
                  )}
                </div>
              </div>
            )}

            {/* Pre-existing value — shown for all types except vibration */}
            {preLabel != null &&
              !(deviceType === "tilting" && tiltEntryMode === "movement") && (
                <div
                  className={
                    deviceType === "crack" || isMovementType(deviceType)
                      ? "grid cols-2"
                      : ""
                  }
                  style={{ gap: 12 }}
                >
                  <div className="field">
                    <label>{preLabel}</label>
                    <input
                      className="input"
                      type="number"
                      step="any"
                      value={preExisting}
                      onChange={(e) => setPreExisting(e.target.value)}
                    />
                    {deviceType === "tilting" && Number(preExisting) !== 0 && (
                      <small style={{ color: "var(--text-muted, #888)" }}>
                        Stored as 1/{Math.round(Number(preExisting))} ={" "}
                        {(1 / Number(preExisting)).toFixed(6)} rad
                      </small>
                    )}
                    {[
                      "ground_settlement",
                      "building_settlement",
                      "utility_settlement",
                    ].includes(deviceType) && (
                      <span
                        style={{
                          fontSize: "0.78em",
                          color: "var(--text-muted, #888)",
                          marginTop: 3,
                          display: "block",
                        }}
                      >
                        −ve: Settlement &nbsp;/&nbsp; +ve: Heave
                      </span>
                    )}
                  </div>
                  {(deviceType === "crack" || isMovementType(deviceType)) && (
                    <div className="field">
                      <label>
                        {isMovementType(deviceType)
                          ? "Pre-existing northing change (mm)"
                          : "Pre-existing value Y (mm)"}
                      </label>
                      <input
                        className="input"
                        type="number"
                        step="any"
                        value={preExisting2}
                        onChange={(e) => setPreExisting2(e.target.value)}
                      />
                    </div>
                  )}
                  {isMovementType(deviceType) && (
                    <div className="field">
                      <label>Pre-existing easting change (mm)</label>
                      <input
                        className="input"
                        type="number"
                        step="any"
                        value={preExisting3}
                        onChange={(e) => setPreExisting3(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

            {/* Ground water alert levels */}
            {deviceType === "ground_water" && (
              <>
                <div
                  style={{
                    borderTop: "1px solid var(--border, #e5e7eb)",
                    paddingTop: 8,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>
                    Ground Water Alert Levels (mPD)
                  </div>
                  <div
                    style={{
                      fontSize: "0.82em",
                      color: "var(--text-muted, #888)",
                      marginBottom: 8,
                    }}
                  >
                    Lowest &amp; Highest are absolute values. The two middle
                    levels are deltas from those bounds.
                  </div>
                </div>
                <div className="grid cols-2" style={{ gap: 12 }}>
                  <div className="field">
                    <label>Lowest (mPD)</label>
                    <input
                      className="input"
                      type="number"
                      step="any"
                      value={gwLowest}
                      onChange={(e) => setGwLowest(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Level above the lowest (delta, m)</label>
                    <input
                      className="input"
                      type="number"
                      step="any"
                      value={gwAboveLowest}
                      onChange={(e) => setGwAboveLowest(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Level below the highest (delta, m)</label>
                    <input
                      className="input"
                      type="number"
                      step="any"
                      value={gwBelowHighest}
                      onChange={(e) => setGwBelowHighest(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Highest (mPD)</label>
                    <input
                      className="input"
                      type="number"
                      step="any"
                      value={gwHighest}
                      onChange={(e) => setGwHighest(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Movement configuration */}
            {isMovementType(deviceType) && (
              <>
                <div className="grid cols-2" style={{ gap: 12 }}>
                  <div className="field">
                    <label>Scope of Measurement</label>
                    <select
                      className="select"
                      value={movementMode}
                      onChange={(e) => {
                        setMovementMode(e.target.value as "1" | "2" | "3");
                      }}
                    >
                      <option value="2">Horizontal Only</option>
                      <option value="1">Vertical (Settlement) Only</option>
                      <option value="3">
                        Horizontal + Vertical (Settlement)
                      </option>
                    </select>
                  </div>
                  {["2", "3"].includes(movementMode) && (
                    <div className="field">
                      <label>Lateral input</label>
                      <input
                        className="input"
                        value="Northing + Easting"
                        disabled
                        readOnly
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!canSave || isLoading}
          >
            {isLoading ? "Saving…" : "Create Checkpoint"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DevicePlacementModal;
